import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook para conexión WebSocket con auto-reconnect y ping-pong
 * @param {string} url - URL del WebSocket (con token en query)
 * @param {function} onMessage - callback(event) para cada mensaje recibido
 * @param {boolean} enabled - si se debe conectar
 */
export function useWebSocket(url, onMessage, enabled = true) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (!enabled || !url) return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Conectado');
        // Iniciar ping cada 30s para mantener conexión viva
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          if (event.data === 'pong') return;
          const parsed = JSON.parse(event.data);
          onMessageRef.current?.(parsed);
        } catch (e) {
          // Ignorar mensajes no-JSON
        }
      };

      ws.onerror = (err) => {
        console.warn('[WS] Error:', err);
      };

      ws.onclose = () => {
        console.log('[WS] Desconectado, reintentando en 3s...');
        clearInterval(pingIntervalRef.current);
        if (enabled) {
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };
    } catch (e) {
      console.error('[WS] Error creando conexión:', e);
    }
  }, [url, enabled]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  return wsRef.current;
}
